import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, ExternalLink, Send, Bookmark, Mail, AlertCircle, EyeOff, Plus } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { NODE_TYPES } from '../../constants/nodeTypes'
import { getDisclosureAgreements, getPersonalLists, addListItem } from '../../services/api'
import DisclosureLevel from './DisclosureLevel'
import DisclosureRequestModal from './DisclosureRequestModal'
import type { ConnectionsNode, DisclosureAgreement, PersonalItem } from '../../types'

interface NodeDetailPanelProps {
  node: ConnectionsNode
  onClose: () => void
  onDiscoveryStart: () => void
}

// BUG-114: node types that map onto a personal-list resource type. Only these
// can genuinely be saved; the rest get an honest disabled control.
const LIST_RESOURCE_TYPE: Partial<Record<ConnectionsNode['type'], PersonalItem['resource_type']>> = {
  document: 'document',
  invention: 'document',
  journal: 'journal',
  project: 'project',
}

export default function NodeDetailPanel({ node, onClose, onDiscoveryStart }: NodeDetailPanelProps) {
  const typeConfig = NODE_TYPES[node.type]
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showDisclosureModal, setShowDisclosureModal] = useState(false)
  // BUG-114: ids added ONLY after the API confirms persistence (previously
  // this was set on click with no API call — a fake "Saved").
  const [savedList, setSavedList] = useState<string[]>([])
  const [showListPicker, setShowListPicker] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [_dismissedSet, setDismissedSet] = useState<Set<string>>(new Set())

  const listResourceType = LIST_RESOURCE_TYPE[node.type]

  // Personal lists — fetched only once the picker opens.
  const { data: personalLists } = useQuery({
    queryKey: ['lists'],
    queryFn: getPersonalLists,
    enabled: showListPicker,
  })

  const saveMutation = useMutation({
    mutationFn: (listId: string) =>
      addListItem(listId, {
        resource_id: node.id,
        resource_type: listResourceType,
        title: node.label,
        notes: '',
      }),
    onSuccess: (_item, listId) => {
      setSavedList(prev => (prev.includes(node.id) ? prev : [...prev, node.id]))
      setSaveError(null)
      setShowListPicker(false)
      queryClient.invalidateQueries({ queryKey: ['lists'] })
      queryClient.invalidateQueries({ queryKey: ['list-items', listId] })
    },
    onError: () => setSaveError('Failed to save to list. Please try again.'),
  })

  const handleDismiss = () => {
    setDismissedSet(prev => new Set([...prev, node.id]))
    onClose()
  }

  // BUG-114: View Details previously had no handler at all.
  const handleViewDetails = () => {
    navigate(node.type === 'journal' ? `/journals/${encodeURIComponent(String(node.id))}` : `/project/${node.id}`)
  }

  const { data: agreements } = useQuery({
    queryKey: ['disclosure-agreements'],
    queryFn: () => getDisclosureAgreements(),
    enabled: node.type === 'discovery',
  })

  // Find matching agreement for this discovery node (by node id as org reference)
  const agreement = agreements?.find(
    (a: DisclosureAgreement) => a.org_a_id === node.id || a.org_b_id === node.id
  )

  return (
    <div className="light-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Node Detail</h3>
        <button onClick={onClose} className="p-0.5 text-gray-500 hover:text-gray-600">
          <X size={14} />
        </button>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: typeConfig?.color || '#6B7280' }}>
            <div className="w-3 h-3 bg-white/30 rounded-sm" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">{node.label}</p>
            <p className="text-xs text-gray-500 capitalize">{typeConfig?.label || node.type}</p>
          </div>
        </div>

        {node.score !== undefined && (
          <div>
            <span className="text-xs text-gray-500 block">Relevance Score</span>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-1.5 bg-sky-200 rounded-full overflow-hidden">
                <div className="h-full bg-iflow-500 rounded-full" style={{ width: `${node.score * 100}%` }} />
              </div>
              <span className="text-xs text-gray-600">{(node.score * 100).toFixed(0)}%</span>
            </div>
          </div>
        )}

        {node.type === 'discovery' && agreement && (
          <DisclosureLevel
            currentLevel={agreement.current_level}
            pendingAdvance={agreement.org_a_consented_level !== agreement.org_b_consented_level}
            onRequestAdvance={() => setShowDisclosureModal(true)}
          />
        )}

        {/* Type-specific metadata for newly expanded types */}
        {node.type === 'invention' && (
          <div className="text-xs text-gray-500">
            <span className="px-2 py-0.5 bg-sky-100 text-sky-700 rounded-full">Invention</span>
          </div>
        )}

        {node.type === 'collaborator' && (
          <div className="text-xs text-gray-500">
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">Collaborator</span>
          </div>
        )}

        {node.type === 'topic' && (
          <div className="text-xs text-gray-500">
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">Topic</span>
          </div>
        )}

        {node.type === 'entity' && (
          <div className="text-xs text-gray-500">
            <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full capitalize">
              {node.subtype || 'Entity'}
            </span>
          </div>
        )}

        {node.type === 'document' && (
          <div className="text-xs text-gray-500">
            <span className="px-2 py-0.5 bg-sky-100 text-sky-700 rounded-full">Document</span>
          </div>
        )}

        {node.type === 'discovery' && (
          <button
            onClick={onDiscoveryStart}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 transition-colors"
          >
            <Send size={12} /> Request Connection
          </button>
        )}

        {(node.type === 'journal' || node.type === 'project') && (
          <button
            onClick={handleViewDetails}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-iflow-600 border border-iflow-300 rounded-lg hover:bg-iflow-50 transition-colors"
          >
            <ExternalLink size={12} /> View Details
          </button>
        )}

        {/* 4 universal action buttons — Wave 2 Stream 2.3 */}
        <div className="border-t border-gray-100 pt-3 space-y-2">
          {listResourceType ? (
            <div className="relative">
              <button
                onClick={() => { if (!savedList.includes(node.id)) { setShowListPicker(v => !v); setSaveError(null) } }}
                disabled={saveMutation.isPending}
                className={`w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors ${savedList.includes(node.id) ? 'bg-iflow-100 text-iflow-700' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'} disabled:opacity-60`}
              >
                <Bookmark size={11} />{' '}
                {savedList.includes(node.id) ? 'Saved' : saveMutation.isPending ? 'Saving…' : 'Save to list'}
              </button>
              {showListPicker && !savedList.includes(node.id) && (
                <div className="mt-1 border border-gray-200 rounded-lg bg-white shadow-sm py-1 max-h-40 overflow-y-auto" data-testid="node-list-picker">
                  {personalLists?.length ? (
                    personalLists.map((l) => (
                      <button
                        key={l.list_id}
                        onClick={() => saveMutation.mutate(l.list_id)}
                        disabled={saveMutation.isPending}
                        className="w-full flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-700 hover:bg-iflow-50 text-left disabled:opacity-60"
                      >
                        <Plus size={10} className="text-gray-500" /> {l.name}
                      </button>
                    ))
                  ) : (
                    <p className="px-3 py-1.5 text-xs text-gray-500">No lists yet — create one under My Lists.</p>
                  )}
                </div>
              )}
              {saveError && <p className="mt-1 text-xs text-red-600" role="alert">{saveError}</p>}
            </div>
          ) : (
            <button
              disabled
              title="Saving to a list is not available for this item type."
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-gray-50 text-gray-400 cursor-not-allowed opacity-60"
            >
              <Bookmark size={11} /> Save to list
            </button>
          )}
          <button
            disabled
            title="Activates in Phase 2 (consent-gated reveal)"
            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-gray-50 text-gray-400 cursor-not-allowed opacity-60"
          >
            <Mail size={11} /> Add to outreach
          </button>
          {/* BUG-114: previously flipped to "Marked as prior art" in component
              state only — nothing was persisted, a fake success. There is no
              prior-art write API yet, so this is an honest pending control. */}
          <button
            disabled
            title="Prior-art marking is coming soon — it cannot be persisted yet."
            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-gray-50 text-gray-400 cursor-not-allowed opacity-60"
          >
            <AlertCircle size={11} /> Mark as prior art
          </button>
          <button
            onClick={handleDismiss}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-gray-50 text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <EyeOff size={11} /> Dismiss as irrelevant
          </button>
        </div>
      </div>

      {showDisclosureModal && agreement && (
        <DisclosureRequestModal
          isOpen={showDisclosureModal}
          onClose={() => setShowDisclosureModal(false)}
          agreement={agreement}
          onConsent={() => {}}
          onReject={() => {}}
        />
      )}
    </div>
  )
}
